import useSWRImmutable from 'swr/immutable';
import settings from '/@/core/settings';
import { axFetch } from '/@/store/session/actions';

export type ZonalYield = {
    /** Historical average yield of the field's department, in kg/ha. */
    value: number;
    /** How far the reference grid point is from the field. */
    distanceKm: number;
    /**
     * The zone the value was resolved for - it is what carries the planting window.
     * `shortName` is a translated field, so it arrives keyed by language and has to
     * go through `_()` before it is rendered.
     */
    zone: {
        id: number;
        codename: string;
        shortName: Record<string, string>;
    };
};

/** The API renders camelCase (`CamelCaseJSONRenderer`), so this mirrors `ZonalYield`. */
type ZonalYieldResponse = ZonalYield;

const getZonalYield = async ([, lat, lng, crop, plantingDate]: [string, number, number, string, string]): Promise<
    ZonalYield | undefined
> => {
    const url = new URL(`${settings.urls.apiDomain}/core/v1/zonal-yield/`, window.location.origin);
    url.search = new URLSearchParams({
        lat: String(lat),
        lng: String(lng),
        crop,
        planting_date: plantingDate,
    }).toString();

    const { data } = await axFetch.get<ZonalYieldResponse>(url.toString());
    if (!data || data.value == null) return undefined;

    return data;
};

/**
 * Historical average yield at the grid point nearest the field, for the planting window its
 * planting date falls in.
 *
 * A field with no point within range resolves to `undefined` rather than to a default, so the
 * caller must render nothing rather than a zero. Immutable: the underlying figure only changes
 * when Bayer ships a new delivery, so it is not worth revalidating on focus.
 */
export const useZonalYield = ({
    lat,
    lng,
    crop = 'corn',
    plantingDate,
}: {
    lat?: number;
    lng?: number;
    crop?: 'corn' | 'soy';
    plantingDate?: string;
}): { zonalYield?: ZonalYield; isLoading: boolean } => {
    const key = lat != null && lng != null && plantingDate ? ['zonal-yield', lat, lng, crop, plantingDate] : null;

    const { data, isLoading } = useSWRImmutable(key, getZonalYield, {
        // Out-of-coverage fields are the common case, and a 404 there is an answer, not a fault.
        shouldRetryOnError: false,
    });

    return { zonalYield: data, isLoading };
};
